// frontend/packages/business-console/src/components/AiUsageMeter.jsx
//
// Reusable, self-fetching meter for the tenant's month-to-date AI/API
// usage (GET /api/v2/ai-usage). One component, three presentations:
//
//   - variant="compact" → a small clickable chip for the app header.
//   - variant="card"    → a Paper card (e.g. on the Billing page).
//   - variant="full"    → card + per-api spend breakdown (the AI Usage page).
//
// The capped, governing resource is monthly USD spend vs the 7.6 cap, so
// that's the headline + the bar. AI tokens are shown as a friendly
// secondary "how much you're using" figure. Unconfigured tenants (no cap
// provisioned) see usage without a limit bar — degrade-open, matching the
// backend.

import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { Link as RouterLink } from 'react-router-dom';

import { BusinessContext } from '../contexts/BusinessContext';
import { getAiUsage } from '../services/aiUsageService';

function formatUsd(n) {
  const v = Number(n) || 0;
  if (v <= 0) return '$0.00';
  if (v < 0.01) return '<$0.01';
  return v.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function barColor(usage) {
  if (!usage || !usage.configured) return 'primary';
  if (usage.overHard) return 'error';
  if (usage.overSoft) return 'warning';
  return 'primary';
}

export default function AiUsageMeter({ variant = 'card' }) {
  const { business } = useContext(BusinessContext);
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getAiUsage()
      .then((data) => {
        if (alive) setUsage(data);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const pct =
    usage && usage.configured && usage.capUsd > 0
      ? Math.min(100, Math.round(usage.pctOfCap))
      : 0;

  // ── compact (header chip) ────────────────────────────────────────
  if (variant === 'compact') {
    if (loading || error || !usage) return null; // stay quiet until we have data
    const label = usage.configured
      ? `AI ${formatUsd(usage.spendUsd)} / ${formatUsd(usage.capUsd)}`
      : `AI ${formatUsd(usage.spendUsd)}`;
    return (
      <Tooltip
        title={
          `${usage.aiTokens.toLocaleString()} AI tokens this month` +
          (usage.configured ? ` · ${pct}% of cap` : '') +
          ` · resets ${formatDate(usage.resetsAt)}`
        }
      >
        <Chip
          size="small"
          icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />}
          label={label}
          variant="outlined"
          color={usage.overSoft ? barColor(usage) : 'default'}
          component={RouterLink}
          to={`${basePath}/ai-usage`}
          clickable
          sx={{ fontWeight: 600 }}
        />
      </Tooltip>
    );
  }

  // ── card / full ──────────────────────────────────────────────────
  const inner = loading ? (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <CircularProgress size={20} />
    </Box>
  ) : error ? (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      Couldn&apos;t load usage right now.
    </Typography>
  ) : !usage ? null : (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 0.5 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {usage.configured ? (
            <>
              {formatUsd(usage.spendUsd)}{' '}
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                of {formatUsd(usage.capUsd)}
              </Box>
            </>
          ) : (
            <>{formatUsd(usage.spendUsd)} used</>
          )}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Resets {formatDate(usage.resetsAt)}
        </Typography>
      </Stack>

      {usage.configured ? (
        <LinearProgress
          variant="determinate"
          value={pct}
          color={barColor(usage)}
          sx={{ height: 8, borderRadius: 4 }}
        />
      ) : (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          No monthly limit set — usage shown for your records.
        </Typography>
      )}

      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', mt: 1 }}
      >
        {usage.aiTokens.toLocaleString()} AI tokens this month
        {usage.configured ? ` · ${pct}% of your allowance` : ''}
      </Typography>

      {variant === 'full' && usage.bySurface && usage.bySurface.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Breakdown
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {usage.bySurface.map((s) => (
              <Stack
                key={s.key}
                direction="row"
                justifyContent="space-between"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {s.key}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatUsd(s.costUsd)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : null}
    </>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <AutoAwesomeRoundedIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          This month&apos;s AI usage
        </Typography>
      </Stack>
      {inner}
    </Paper>
  );
}
